/**
 * AskPanel — streaming chat with the Norris guide assistant.
 * All message state is lifted into HelpLauncher; this component is purely presentational
 * plus the send/stream/abort orchestration.
 */

import { useRef, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import ReactMarkdown from '../markdown/SafeMarkdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { streamGuideChat, type GuideChatMessage } from '../../api/guide';
import { CornerDownLeft, Trash2 } from 'lucide-react';

const PRIMARY = 'var(--primary)';

// ── Minimal safe markdown components (no rehype-raw) ────────────────────────

const mdComponents: Components = {
  p: ({ children }) => (
    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', lineHeight: 1.6 }}>{children}</p>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.25rem' }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '0 0 0.5rem', paddingLeft: '1.5rem' }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: '0.2rem', fontSize: '0.875rem', lineHeight: 1.6 }}>{children}</li>
  ),
  code: ({ children }) => (
    <code
      style={{
        display: 'inline-block',
        padding: '1px 5px',
        background: 'var(--surface-strong)',
        border: '1px solid var(--border)',
        borderRadius: 3,
        fontFamily: 'monospace',
        fontSize: '0.8rem',
      }}
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre
      style={{
        background: 'var(--surface-strong)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '0.6rem 0.75rem',
        overflowX: 'auto',
        marginBottom: '0.5rem',
        fontSize: '0.8rem',
      }}
    >
      {children}
    </pre>
  ),
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote
      style={{
        background: 'rgba(90, 140, 143, 0.1)',
        border: '1px solid rgba(90, 140, 143, 0.25)',
        borderRadius: 8,
        padding: '0.5rem 0.75rem',
        marginBottom: '0.5rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </blockquote>
  ),
};

// ── Screen name derivation ───────────────────────────────────────────────────

function deriveScreen(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  // /:dbId/<screen> or /:dbId/<screen>/:id
  if (parts.length >= 2) {
    const screen = parts[1];
    const screens: Record<string, string> = {
      documents: 'Document List',
      ingest: 'Upload',
      ingestions: 'Ingestion Status',
      tags: 'Tags',
      entities: 'Entities',
      folders: 'Folders',
      users: 'Users',
      settings: 'Settings',
      review: 'Review',
      productions: 'Productions',
      help: 'Help',
      guide: 'Guide',
      'ai-tag-queue': 'AI Tag Queue',
    };
    if (screen === 'review' && parts.length === 3 && parts[2] === 'qc') return 'QC Queue';
    if (screen === 'documents' && parts.length === 3) return 'Document Viewer';
    if (screen === 'review' && parts.length >= 3) return 'Batch Review';
    if (screen === 'productions' && parts.length === 3) return 'Production Detail';
    if (screen === 'ingestions') return 'Ingestion Status';
    return screens[screen] ?? screen;
  }
  if (parts.length === 1) return 'Database Home';
  return 'Home';
}

// ── AskPanel ─────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  error?: string;
}

interface AskPanelProps {
  messages: ChatMessage[];
  streaming: boolean;
  abortRef: React.MutableRefObject<AbortController | null>;
  onMessagesChange: (next: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  onStreamingChange: (v: boolean) => void;
  draft: string;
  onDraftChange: (v: string) => void;
}

export default function AskPanel({
  messages,
  streaming,
  abortRef,
  onMessagesChange,
  onStreamingChange,
  draft,
  onDraftChange,
}: AskPanelProps) {
  const location = useLocation();
  const { dbId } = useParams<{ dbId?: string }>();
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  function buildHistory(): GuideChatMessage[] {
    return messages
      .filter((m) => m.role === 'user' || (m.role === 'assistant' && !m.error))
      .map((m) => ({ role: m.role, content: m.content }));
  }

  async function send() {
    const question = draft.trim();
    if (!question || streaming) return;

    const history = buildHistory();
    const context = {
      route: location.pathname + location.search,
      screen: deriveScreen(location.pathname),
      ...(dbId ? { database_id: dbId } : {}),
    };

    // Append user message + empty assistant placeholder
    const next: ChatMessage[] = [
      ...messages,
      { role: 'user', content: question },
      { role: 'assistant', content: '' },
    ];
    onMessagesChange(next);
    onDraftChange('');
    onStreamingChange(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const assistantIdx = next.length - 1;

    await streamGuideChat(
      { question, history, context },
      {
        signal: controller.signal,
        onDelta: (text) => {
          onMessagesChange((prev: ChatMessage[]) => {
            const copy = [...prev];
            copy[assistantIdx] = {
              ...copy[assistantIdx],
              content: copy[assistantIdx].content + text,
            };
            return copy;
          });
        },
        onDone: () => {
          onStreamingChange(false);
          abortRef.current = null;
        },
        onError: (message) => {
          onMessagesChange((prev: ChatMessage[]) => {
            const copy = [...prev];
            copy[assistantIdx] = {
              ...copy[assistantIdx],
              error: message,
              content: copy[assistantIdx].content || '',
            };
            return copy;
          });
          onStreamingChange(false);
          abortRef.current = null;
        },
      },
    );
  }

  function handleClear() {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    onStreamingChange(false);
    onMessagesChange([]);
    onDraftChange('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const canSend = draft.trim().length > 0 && !streaming;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Message list */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '12px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: '0.85rem',
              marginTop: 24,
            }}
          >
            Ask anything about Valentine — how to manage cases, generate documents, track clients, and more.
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '88%',
                padding: '8px 12px',
                borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                background:
                  msg.role === 'user'
                    ? PRIMARY
                    : 'var(--surface-strong)',
                color:
                  msg.role === 'user' ? 'var(--text-inverse)' : 'var(--text-primary)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
              }}
            >
              {msg.role === 'user' ? (
                <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
              ) : (
                <>
                  {msg.content && (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  )}
                  {!msg.content && !msg.error && streaming && i === messages.length - 1 && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 14,
                        background: 'var(--primary)',
                        borderRadius: 2,
                        animation: 'norris-blink 0.9s step-end infinite',
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                  {msg.error && (
                    <span
                      style={{
                        color: 'var(--danger)',
                        fontSize: '0.8125rem',
                        fontStyle: 'italic',
                      }}
                    >
                      {msg.error}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Input area */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <textarea
          ref={textareaRef}
          rows={3}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => e.stopPropagation()}
          placeholder="Ask a question… (Enter to send, Shift+Enter for newline)"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '7px 10px',
            borderRadius: 4,
            border: '1px solid var(--border)',
            fontSize: 14,
            resize: 'none',
            fontFamily: 'inherit',
            background: 'var(--surface-strong)',
            color: 'var(--text-primary)',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={handleClear}
            title="Clear chat"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              borderRadius: 4,
              border: '1px solid var(--border)',
              background: 'var(--surface-strong)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            <Trash2 size={13} />
            Clear
          </button>
          <button
            onClick={send}
            disabled={!canSend}
            title="Send (Enter)"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 14px',
              borderRadius: 4,
              border: 'none',
              background: canSend ? PRIMARY : 'var(--disabled-bg)',
              color: canSend ? 'var(--text-inverse)' : 'var(--text-secondary)',
              cursor: canSend ? 'pointer' : 'not-allowed',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <CornerDownLeft size={14} />
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
